import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api'
import { ChatSummarySchema, PaginatedChatSummariesResponse } from '../../types/chatTypes'

const generateConversationPreview = (message: string): string => {
  if (!message) return ''

  // Use the first 50 characters of the message as the preview
  return message.substring(0, 50) + (message.length > 50 ? '...' : '')
}

interface UseChatsOptions {
  searchQuery?: string
  pageSize?: number
}

export const useChats = ({ searchQuery = '', pageSize = 20 }: UseChatsOptions = {}) => {
  const queryClient = useQueryClient()
  const trimmedQuery = searchQuery.trim()
  const isSearchMode = trimmedQuery.length > 0

  // Use different query keys for search vs regular mode to maintain separate caches
  const queryKey = isSearchMode ? ['chatSearch', trimmedQuery] : ['chatSummaries']

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      if (isSearchMode) {
        return await apiClient.chatHistory.searchChatSummaries(trimmedQuery, pageParam, pageSize)
      } else {
        return await apiClient.chatHistory.fetchChatSummaries(pageParam, pageSize)
      }
    },
    getNextPageParam: (lastPage: PaginatedChatSummariesResponse) => {
      // Return next page number if there are more pages, otherwise undefined
      return lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined
    },
    initialPageParam: 1,
    enabled: !isSearchMode || trimmedQuery.length > 0, // Always enabled for regular mode, only when query exists for search
    staleTime: 30000,
    refetchOnWindowFocus: !isSearchMode,
    // Poll every 60s so a new chat in one browser surfaces in another for the same user.
    // A push-based alternative (SSE) would be instant but requires bigger changes.
    refetchInterval: isSearchMode ? false : 60000,
    refetchIntervalInBackground: false,
  })

  const chatList: ChatSummarySchema[] = data?.pages.flatMap(page => page.items) ?? []

  const updateChatInList = (chatId: string, message: string) => {
    if (isSearchMode) {
      return
    }

    const preview = generateConversationPreview(message)

    queryClient.setQueryData(['chatSummaries'], (oldData: any) => {
      if (!oldData?.pages) return oldData

      return {
        ...oldData,
        pages: oldData.pages.map((page: PaginatedChatSummariesResponse) => ({
          ...page,
          items: page.items.map((chat: ChatSummarySchema) =>
            chat.id === chatId
              ? {
                  ...chat,
                  lastUpdated: new Date().toISOString(),
                  preview: preview,
                }
              : chat,
          ),
        })),
      }
    })
  }

  return {
    // Data
    chatList,
    totalChats: data?.pages[0]?.total ?? 0,

    // States
    isLoading,
    error: error ? (isSearchMode ? 'Failed to search chat history' : 'Failed to load chat history') : null,
    isSearchMode,

    // Pagination
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Actions
    refetch,
    updateChatInList,

    // Convenience properties
    hasResults: chatList.length > 0,
    isEmpty: !isLoading && chatList.length === 0,
  }
}
